/**
 * BrokerageFunnelChart — the lead funnel for one brokerage.
 *
 * Renders the lead lifecycle stages from MoM 05 Jun 2026 §7:
 * New → Prospecting → Viewing → Negotiation → Booking Initiated →
 * Won, plus the terminal Lost bar on the far right. ``contacted`` is
 * the internal key for the "Prospecting" stage (no DB migration), so
 * the chart still keys off that name.
 */
import React from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, LabelList,
} from 'recharts';

// Stage order + colour. Matches HUB_LEAD_FUNNEL in
// routes/brokerage_hub_routes.py, with Lost appended at the end so
// the chart shows the complete lifecycle in one strip. The
// ``booking_initiated`` stage gets a deep indigo so it visually sits
// between Negotiation (orange) and Won (green) without recolouring
// the rest of the funnel.
const STAGES = [
  { key: 'new',               label: 'New',               color: '#94A3B8' },
  { key: 'contacted',         label: 'Prospecting',       color: '#3B82F6' },
  { key: 'viewing',           label: 'Viewing',           color: '#F59E0B' },
  { key: 'negotiation',       label: 'Negotiation',       color: '#F97316' },
  { key: 'booking_initiated', label: 'Booking Initiated', color: '#6366F1' },
  { key: 'won',               label: 'Won',               color: '#22C55E' },
  { key: 'lost',              label: 'Lost',              color: '#EF4444' },
];

export default function BrokerageFunnelChart({ funnel }) {
  const data = STAGES.map((s) => ({
    name: s.label,
    value: funnel?.[s.key] || 0,
    color: s.color,
  }));
  const hasAny = data.some((d) => d.value > 0);

  if (!hasAny) {
    return (
      <div className="h-[240px] flex items-center justify-center text-sm text-slate-400">
        No leads in the funnel yet.
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={240}>
      <BarChart data={data} margin={{ top: 16, right: 16, left: 0, bottom: 4 }}>
        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
        <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
        <Tooltip cursor={{ fill: 'rgba(148,163,184,0.12)' }} />
        <Bar dataKey="value" name="Leads" radius={[4, 4, 0, 0]}>
          {data.map((d) => (
            <Cell key={d.name} fill={d.color} />
          ))}
          <LabelList dataKey="value" position="top" style={{ fontSize: 11 }} />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}
